import { Box } from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  MdOutlineCollectionsBookmark,
  MdOutlineFolderShared,
  MdOutlinePublic,
  MdOutlineStarPurple500,
  MdUpload,
} from 'react-icons/md';
import {
  RiArrowLeftDoubleFill,
  RiArrowRightDoubleFill,
  RiBuildingLine,
  RiFileAddLine,
  RiRecordCircleLine,
} from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router';

import useWindowResize from '../hooks/useWindowResize';
import { useStoreActions, useStoreState } from '../reducers';

const navContainerSx = {
  display: 'grid',
};

const indicatorSx = {
  width: '8px',
  height: '8px',
  backgroundColor: '#22c55e',
  borderRadius: '50%',
  marginLeft: 'auto',
  flexShrink: 0,
};

const getToggleSx = (collapsed: boolean) => ({
  height: '2rem',
  display: 'none',
  justifyContent: 'center',
  padding: 0,
  alignItems: 'center',
  fontSize: '20px',
  cursor: 'pointer',
  margin: '-1rem 0 0',
  borderBottom: '1px solid var(--ouster-colors-gray-700)',
  '@media (max-width: 1400px)': {
    display: 'flex',
    justifyContent: collapsed ? 'center' : 'flex-end',
    paddingRight: collapsed ? 0 : '1rem',
    backgroundColor: '#5e79fb24',
  },
});

const getActionButtonSx = (
  collapsed: boolean,
  variant: 'solid' | 'outline'
) => ({
  textAlign: 'left',
  display: 'flex',
  fontSize: '14px',
  marginInline: '1rem',
  columnGap: '0.5rem',
  borderRadius: '4px',
  alignItems: 'center',
  padding: '0.5rem 0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  lineHeight: 1,
  backgroundColor:
    variant === 'solid' ? 'var(--color-blast-off-blue)' : 'transparent',
  border: '1px solid',
  borderColor:
    variant === 'solid' ? 'transparent' : 'var(--color-blast-off-blue)',
  color: variant === 'solid' ? 'white' : 'var(--color-blast-off-blue)',
  '& svg': {
    fontSize: '24px',
  },
  '&:hover': {
    filter: 'brightness(130%)',
  },
  '@media (max-width: 1400px)': {
    '& span': {
      display: collapsed ? 'none' : 'flex',
    },
  },
});

const getNavLinkSx = (
  isActive: boolean,
  collapsed: boolean,
  options?: { isSubmenu?: boolean; hideWhenCollapsed?: boolean }
) => ({
  textAlign: 'left',
  display: 'flex',
  fontSize: '14px',
  columnGap: '0.5rem',
  alignItems: 'center',
  padding: options?.isSubmenu
    ? '0.75rem 1.75rem 0.75rem 2.75rem'
    : '0.85rem 1.75rem',
  borderBottom: '1px solid var(--ouster-colors-whiteAlpha-200)',
  fontWeight: options?.isSubmenu ? 400 : 500,
  whiteSpace: 'nowrap',
  cursor: isActive ? 'default' : 'pointer',
  lineHeight: 1,
  backgroundColor: isActive ? 'var(--ouster-colors-blue-700)' : 'transparent',
  color: isActive ? 'white' : 'var(--color-gray-4)',
  '& svg': {
    fontSize: '24px',
  },
  '&:hover': {
    backgroundColor: isActive
      ? 'var(--ouster-colors-blue-700)'
      : 'var(--ouster-colors-whiteAlpha-200)',
    color: isActive ? 'white' : 'var(--ouster-colors-blue-400)',
  },
  '@media (max-width: 1400px)': {
    display: options?.hideWhenCollapsed && collapsed ? 'none' : 'flex',
    '& span': {
      display: collapsed ? 'none' : 'flex',
    },
  },
});

interface Tab {
  name: string;
  path: string;
  icon: JSX.Element;
  indicator?: boolean;
  submenus?: {
    icon: JSX.Element;
    name: string;
    path: string;
    indicator?: boolean;
  }[];
}

const OUSTER_ONLY_TABS = ['Architect Scenes'];
const SUPPORT_ONLY_TABS = ['Shared with Ouster'];

type SideNavMode = 'private' | 'public';
interface SideNavProps {
  mode?: SideNavMode;
}

/**
 *
 */
export default function SideNav({ mode = 'private' }: SideNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { curUserOrg, isOusterUser } = useStoreState((storeState) => ({
    ...storeState.user,
  }));
  const { newContentStatus } = useStoreState((storeState) => ({
    ...storeState.drive,
  }));

  const {
    // setIsOpen: setUploadVisibility,
    setIsPublicWarningOpen,
    setUploadModalStatus,
  } = useStoreActions((actions) => actions.recordingUpload);

  const { width } = useWindowResize();
  const [collapsed, setCollapsed] = useState(width <= 1400);

  const tabs: Tab[] = useMemo(
    () =>
      mode === 'public'
        ? [
            {
              name: 'Recordings',
              path: '/public/recordings',
              icon: <RiRecordCircleLine />,
            },
            {
              name: 'Shared with Ouster',
              path: '/public/shared_with_ouster',
              icon: <MdOutlineFolderShared />,
            },
            {
              name: 'Sample Library',
              path: '/public/sample_library',
              icon: <MdOutlineCollectionsBookmark />,
              submenus: [
                {
                  name: 'Public Collection',
                  path: '/public/sample_library/public_collection',
                  icon: <MdOutlinePublic />,
                },
                {
                  name: 'Partner Previews',
                  path: '/public/sample_library/partner_preview',
                  icon: <MdOutlineStarPurple500 />,
                },
              ],
            },
          ]
        : [
            {
              name: 'Recordings',
              path: '/drive/private',
              icon: <RiRecordCircleLine />,
            },
            {
              name: 'Shared with Ouster',
              path: '/shared_with_ouster',
              icon: <MdOutlineFolderShared />,
            },
            {
              name: 'Architect Scenes',
              path: '/architect_scene',
              icon: <RiBuildingLine />,
            },
            {
              name: 'Sample Library',
              path: '/drive/sample_library',
              icon: <MdOutlineCollectionsBookmark />,
              submenus: [
                {
                  name: 'Public Collection',
                  path: '/drive/sample_library/public_collection',
                  icon: <MdOutlinePublic />,
                  indicator: newContentStatus?.PUBLIC_PRIVATE,
                },
                {
                  name: 'Partner Previews',
                  path: '/drive/sample_library/partner_preview',
                  icon: <MdOutlineStarPurple500 />,
                  indicator: newContentStatus?.SPECIFIC_ORGS,
                },
              ],
            },
          ],
    [mode, newContentStatus?.PUBLIC_PRIVATE, newContentStatus?.SPECIFIC_ORGS]
  );

  useEffect(() => {
    // `collapsed` is user-toggleable (see handleToggle) and must stay
    // "sticky" to that manual choice across re-renders at the same width,
    // so it can't be a plain derived value - but it also needs to
    // re-sync to the browser's viewport width (an external signal)
    // whenever that width crosses the collapse breakpoint.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCollapsed(width <= 1400);
  }, [width]);

  function handleToggle() {
    setCollapsed(!collapsed);
  }

  function handleUploadClick() {
    if (mode === 'public') {
      setIsPublicWarningOpen(true);
    } else {
      setUploadModalStatus('ACTIVE_MAX');
    }
  }

  function handleNavClick(path: string, hasSubmenus: boolean) {
    if (hasSubmenus && collapsed) {
      setCollapsed(false);
    }
    navigate(path);
  }

  function isPathActive(path: string, submenus?: any[]): boolean {
    const isSubmenuActive =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      submenus?.some((submenu) => location.pathname === submenu.path) ?? false;

    if (collapsed) {
      // If collapsed, consider the menu active when it matches path or has active submenu
      return location.pathname === path || isSubmenuActive;
    }

    // If expanded, consider menu active only when the path match
    return location.pathname === path;
  }

  function loadNewScene() {
    const url = `${window.location.origin}/architect/?new`;
    window.open(url, '_blank');
  }

  return (
    <Box as="nav" sx={navContainerSx} data-testid="side-nav-root">
      <Box
        as="button"
        type="button"
        onClick={handleToggle}
        sx={getToggleSx(collapsed)}
        data-testid="side-nav-toggle"
      >
        {collapsed ? <RiArrowRightDoubleFill /> : <RiArrowLeftDoubleFill />}
      </Box>

      {/* FUNCTION BUTTONS */}
      <Box
        display="grid"
        rowGap="1rem"
        mt="1rem"
        pb="1.5rem"
        borderBottom="1px solid"
        borderColor="var(--color-unify-white-30)"
        data-testid="side-nav-function-buttons"
      >
        <Tooltip
          title={
            collapsed ? (
              <div style={{ fontSize: 14, padding: 4 }}>Upload Data</div>
            ) : null
          }
          arrow
          placement="right"
          sx={{ fontSize: 34 }}
        >
          <Box
            as="button"
            type="button"
            sx={getActionButtonSx(collapsed, 'solid')}
            onClick={handleUploadClick}
            data-testid="side-nav-upload-btn"
          >
            <MdUpload />
            <span>Upload Data</span>
          </Box>
        </Tooltip>
        {/* 
          TODO: vivian - currently we just simply hide "new architect" button
          for all non-ouster org. we need to a better feature flag to check in the future
        */}
        {curUserOrg?.id === 1 && (
          <Tooltip
            title={
              collapsed ? (
                <div style={{ fontSize: 14, padding: 4 }}>New Architect</div>
              ) : null
            }
            arrow
            placement="right"
            sx={{ fontSize: 34 }}
          >
            <Box
              as="button"
              type="button"
              sx={getActionButtonSx(collapsed, 'outline')}
              onClick={loadNewScene}
              data-testid="side-nav-new-architect-btn"
            >
              <RiFileAddLine />
              <span>New Architect</span>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* NAV LINKS */}
      {tabs.map((tab) => {
        /* 
          Hiding architect scenes for non-ouster users in the first phase of multi tenancy
        */
        if (OUSTER_ONLY_TABS.includes(tab.name) && !isOusterUser) {
          return null;
        }
        if (
          isOusterUser &&
          SUPPORT_ONLY_TABS.includes(tab.name) &&
          curUserOrg?.role === 'BASIC'
        ) {
          return null;
        }
        return (
          <div
            key={tab.name}
            data-testid={`side-nav-tab-${tab.name
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
          >
            <Tooltip
              title={
                collapsed ? (
                  <div style={{ fontSize: 14, padding: 4 }}>{tab.name}</div>
                ) : null
              }
              arrow
              placement="right"
              sx={{ fontSize: 34 }}
            >
              <Box
                as="a"
                sx={getNavLinkSx(
                  isPathActive(tab.path, tab.submenus),
                  collapsed
                )}
                onClick={() => handleNavClick(tab.path, !!tab.submenus)}
                data-testid={`side-nav-link-${tab.name
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`}
              >
                {tab.icon}
                <span>{tab.name}</span>
                {tab.indicator && <Box sx={indicatorSx} />}
              </Box>
            </Tooltip>
            {/* Render submenus if they exist */}
            {tab.submenus && (
              <div
                data-testid={`side-nav-submenus-${tab.name
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`}
              >
                {tab.submenus.map((submenu) => (
                  <Box
                    as="a"
                    key={submenu.name}
                    sx={getNavLinkSx(isPathActive(submenu.path), collapsed, {
                      isSubmenu: true,
                      hideWhenCollapsed: true,
                    })}
                    onClick={() => handleNavClick(submenu.path, false)}
                    data-testid={`side-nav-sublink-${submenu.name
                      .replace(/\s+/g, '-')
                      .toLowerCase()}`}
                  >
                    {submenu.icon}
                    <span>{submenu.name}</span>
                    {submenu.indicator && <Box sx={indicatorSx} />}
                  </Box>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </Box>
  );
}
